import { useTranslation } from '@/hooks/useTranslation';
import { CurrencyDisplay } from './CurrencyDisplay';
import { ArrowDownToLine, Flag } from 'lucide-react';
import { Badge } from '@ophelos/ui/components/badge';

interface PartialPaymentProgressProps {
  originalAmount: number;
  currentBalance: number;
  currency?: string;
}

// Get gradient color based on progress (orange -> amber -> green)
function getProgressColor(progress: number): string {
  let r: number, g: number, b: number;

  if (progress < 50) {
    // First half: Orange -> Amber
    // Orange: rgb(249, 115, 22) -> Amber: rgb(251, 191, 36)
    const t = progress / 50;
    r = Math.round(249 + (251 - 249) * t);
    g = Math.round(115 + (191 - 115) * t);
    b = Math.round(22 + (36 - 22) * t);
  } else {
    // Second half: Amber -> Green
    // Amber: rgb(251, 191, 36) -> Green: rgb(34, 197, 94)
    const t = (progress - 50) / 50;
    r = Math.round(251 - (251 - 34) * t);
    g = Math.round(191 + (197 - 191) * t);
    b = Math.round(36 + (94 - 36) * t);
  }

  return `rgb(${r}, ${g}, ${b})`;
}

export function PartialPaymentProgress({ originalAmount, currentBalance, currency = 'EUR' }: PartialPaymentProgressProps) {
  const { t } = useTranslation();
  const paidAmount = originalAmount - currentBalance;
  const progress = originalAmount > 0 ? (paidAmount / originalAmount) * 100 : 0;

  return (
    <div className="space-y-2 w-full mt-2">
      <div className="h-1.5 w-full bg-muted-foreground/20 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${progress}%`, backgroundColor: getProgressColor(progress) }}
        />
      </div>
      <div className="stack space-y-2 mt-4">
        <div className="flex items-start justify-between text-sm font-medium border-b pb-2 border-border-light">
          <div className="flex items-center gap-2 text-left">
            <Flag className="size-3.5 text-soft-foreground" />
            <span className="text-muted-foreground">{t('paymentPlanProgress.startingBalance')}:</span>
          </div>

          <CurrencyDisplay amount={originalAmount} currency={currency} className="text-foreground font-semibold" />
        </div>

        <div className="flex items-start justify-between text-sm font-medium ">
          <div className="flex items-center gap-2 text-left">
            <ArrowDownToLine className="size-3.5 text-soft-foreground" />
            <span className="text-muted-foreground">{t('paymentPlanProgress.amountPaid')}:</span>
          </div>
          <div className="flex items-center gap-2 text-left">
            <CurrencyDisplay amount={originalAmount - currentBalance} currency={currency} className="text-foreground font-semibold" />
            <Badge variant="subtle">{Math.round(progress).toFixed(0)}%</Badge>
          </div>
        </div>
      </div>
    </div>
  );
}
